import { Flex, useDateTimePickerRange } from '@solinkcorp/solink-components';
import { CommonContext } from 'components/Contexts';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import EventTimePicker from './EventTimePicker';
import './EventTimePicker.sass';

interface EventTimePickerRangeProps {
  timezoneId: string;
  startTime: Date | number | undefined;
  endTime: Date | number | undefined;
  durationLimitsMs?: readonly [number, number];
  disabled?: boolean;
  updateStartTime: (date: Date | undefined) => void;
  updateEndTime: (date: Date | undefined) => void;
  maxDate?: Date | number;
  minDate?: Date | number;
  hideSeconds?: boolean;
  startDateTestId?: string;
  endDateTestId?: string;
}

export default function EventTimePickerRange(props: EventTimePickerRangeProps) {
  const {
    timezoneId,
    durationLimitsMs,
    disabled,
    updateEndTime,
    updateStartTime,
    minDate,
    maxDate,
    hideSeconds,
    startDateTestId,
    endDateTestId,
  } = props;
  const locale = useLocale();
  const calendarStartDay = React.useContext(CommonContext).authData.firstDayOfWeek;

  const startTime = React.useMemo(() => toDate(props.startTime), [props.startTime]);
  const endTime = React.useMemo(() => toDate(props.endTime), [props.endTime]);
  const [startProps, endProps] = useDateTimePickerRange({
    locale,
    calendarStartDay,
    timeZoneId: timezoneId,
    startTime,
    endTime,
    maxDurationMs: durationLimitsMs?.[1],
    disabled,
    onChange([newStartTime, newEndTime]) {
      if (newStartTime?.valueOf() !== startTime?.valueOf()) {
        updateStartTime(newStartTime);
      }
      if (newEndTime?.valueOf() !== endTime?.valueOf()) {
        updateEndTime(newEndTime);
      }
    },
  });

  /** Event's end time should always be later than the start time */
  const minEndDate = React.useMemo(() => {
    if (startTime) {
      return new Date(startTime.valueOf());
    }
    return minDate;
  }, [startTime, minDate]);

  return (
    <Flex gap="0.5rem" height="100%">
      <EventTimePicker
        dateTimePickerProps={{ ...startProps, dataTestId: startDateTestId }}
        title={locale.fields.start}
        minTime={minDate}
        maxTime={maxDate}
        hideSeconds={hideSeconds}
      />
      <EventTimePicker
        dateTimePickerProps={{ ...endProps, dataTestId: endDateTestId }}
        title={locale.fields.end}
        minTime={minEndDate}
        maxTime={maxDate}
        hideSeconds={hideSeconds}
      />
    </Flex>
  );
}

function toDate(date: Date | number | string | undefined): Date | undefined {
  return date === undefined ? undefined : new Date(date);
}
